/* ==========================================================================
   ZARGES USF GRID ENHANCER
   --------------------------------------------------------------------------
   Self-contained script that adds enhancements to product cards rendered
   by the AI Search & Product Filter app (USF/Sparq):

   1. Hover spec overlay with Quick View button (Zarges-branded)
   2. Secondary image swap on hover (cross-fades to product image #2)
   3. Parallax pan on the theme's secondary hover image
   4. Hover-to-play for product card videos

   Approach: instead of extending USF's Vue components, this script waits
   for USF to render its cards, then enhances each card from outside.
   ========================================================================== */

(function() {
  'use strict';

  // ---- Wait for USF to be ready ------------------------------------------
  function waitForUsf(callback) {
    var attempts = 0;
    var interval = setInterval(function() {
      attempts++;
      if (window.usf && window.usf.event && typeof window.usf.event.add === 'function') {
        clearInterval(interval);
        callback();
      } else if (attempts > 100) {
        clearInterval(interval);
        console.warn('[Zarges] USF never became available; enhancement not applied.');
      }
    }, 100);
  }

  // ---- Read a metafield value off a USF product object -------------------
  function getMeta(product, namespace, key) {
    if (!product || !product.metafields) return '';
    var mf = product.metafields.find(function(m) {
      return m.namespace === namespace && m.key === key;
    });
    if (!mf || !mf.value) return '';
    var raw = mf.value;
    try {
      var parsed = JSON.parse(raw);
      return parsed.value || parsed;
    } catch (e) {
      return raw;
    }
  }

  // ---- Build the spec object for a product -------------------------------
  function buildSpecs(product) {
    var ext_l = getMeta(product, 'my_fields', 'external_length');
    var ext_w = getMeta(product, 'my_fields', 'external_width');
    var ext_h = getMeta(product, 'my_fields', 'external_height');
    var int_l = getMeta(product, 'my_fields', 'internal_length');
    var int_w = getMeta(product, 'my_fields', 'internal_width');
    var int_h = getMeta(product, 'my_fields', 'internal_height');
    var volume = getMeta(product, 'my_fields', 'volume');
    var weight = getMeta(product, 'custom', 'weight');

    var exterior = (ext_l && ext_w && ext_h) ? (ext_l + 'in × ' + ext_w + 'in × ' + ext_h + 'in') : '';
    var interior = (int_l && int_w && int_h) ? (int_l + 'in × ' + int_w + 'in × ' + int_h + 'in') : '';

    return {
      exterior: exterior,
      interior: interior,
      volume: volume,
      weight: weight,
      hasAny: !!(exterior || interior || volume || weight)
    };
  }

  // ---- Spec icon SVGs ----------------------------------------------------
  var ICONS = {
    exterior: '<svg class="zarges-spec-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="5" width="18" height="14" rx="1"/><line x1="3" y1="9" x2="21" y2="9"/></svg>',
    interior: '<svg class="zarges-spec-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="5" width="18" height="14" rx="1"/><rect x="6" y="8" width="12" height="8" rx="0.5" stroke-dasharray="2 2"/></svg>',
    volume: '<svg class="zarges-spec-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M5 3h14l-1.5 17a1 1 0 0 1-1 .9H7.5a1 1 0 0 1-1-.9L5 3z"/><path d="M6 11h12" stroke-dasharray="1 2"/></svg>',
    weight: '<svg class="zarges-spec-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M5 8h14l-1.5 12h-11L5 8z"/><circle cx="12" cy="5" r="2"/><path d="M10 5h4"/></svg>'
  };

  // ---- Build the overlay HTML for a product ------------------------------
  function buildOverlayHtml(product, specs) {
    var rows = '';
    if (specs.exterior) {
      rows += '<div class="zarges-spec">' + ICONS.exterior +
              '<div class="zarges-spec-text"><div class="zarges-spec-value">' + specs.exterior + '</div><div class="zarges-spec-label">Exterior</div></div></div>';
    }
    if (specs.interior) {
      rows += '<div class="zarges-spec">' + ICONS.interior +
              '<div class="zarges-spec-text"><div class="zarges-spec-value">' + specs.interior + '</div><div class="zarges-spec-label">Interior</div></div></div>';
    }
    if (specs.volume) {
      rows += '<div class="zarges-spec">' + ICONS.volume +
              '<div class="zarges-spec-text"><div class="zarges-spec-value">' + specs.volume + '</div><div class="zarges-spec-label">Volume</div></div></div>';
    }
    if (specs.weight) {
      rows += '<div class="zarges-spec">' + ICONS.weight +
              '<div class="zarges-spec-text"><div class="zarges-spec-value">' + specs.weight + '</div><div class="zarges-spec-label">Weight</div></div></div>';
    }

    var titleSafe = (product.title || '').replace(/"/g, '&quot;');
    var url = '/products/' + (product.urlName || '');

    var variant = (product.variants && product.variants.length) ? product.variants[0] : null;
    var variantId = variant ? variant.id : '';
    var price = variant ? formatPrice(variant.price) : '';
    var imageUrl = (product.images && product.images.length) ? getImageUrl(product.images[0], '1024') : '';

    return '<div class="zarges-spec-overlay" aria-hidden="true">' +
              '<div class="zarges-spec-grid">' + rows + '</div>' +
              '<button type="button" class="zarges-quickview-btn" ' +
                'data-zarges-quickview ' +
                'data-product-title="' + titleSafe + '" ' +
                'data-product-handle="' + (product.urlName || '') + '" ' +
                'data-product-url="' + url + '" ' +
                'data-product-image="' + imageUrl + '" ' +
                'data-product-price="' + price + '" ' +
                'data-product-variant-id="' + variantId + '" ' +
                'data-exterior="' + specs.exterior + '" ' +
                'data-interior="' + specs.interior + '" ' +
                'data-volume="' + specs.volume + '" ' +
                'data-weight="' + specs.weight + '" ' +
                'aria-label="Quick view for ' + titleSafe + '">' +
                'Quick View ' +
                '<svg viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="2"><path d="M1 7h12M7 1l6 6-6 6"/></svg>' +
              '</button>' +
            '</div>';
  }

  // ---- Find a product by ID in the latest USF result ---------------------
  function findProductById(productId) {
    if (!window.usf || !window.usf.search || !window.usf.search.result) return null;
    var items = window.usf.search.result.items || [];
    for (var i = 0; i < items.length; i++) {
      if (String(items[i].id) === String(productId)) return items[i];
    }
    return null;
  }

  // ---- Get the URL for an image at a given size --------------------------
  function getImageUrl(image, size) {
    if (!image || !image.url) return '';
    var url = image.url;

    if (url.indexOf('//') === 0) {
      url = 'https:' + url;
    }

    if (url.indexOf('shopify.com') === -1 && url.indexOf('shopifycdn') === -1) {
      return url;
    }
    if (url.indexOf('/assets/') !== -1) return url;

    if (size) {
      var separator = url.indexOf('?') === -1 ? '?' : '&';
      url = url.replace(/[?&](width|height)=\d+/g, '');
      separator = url.indexOf('?') === -1 ? '?' : '&';
      url = url + separator + 'width=' + size;
    }

    return url;
  }

  // ---- Format a price using USF's currency converter if available --------
  function formatPrice(price) {
    if (price === undefined || price === null) return '';
    try {
      if (window.usf && window.usf.currencyConverter && window.usf.currencyConverter.format) {
        return window.usf.currencyConverter.format(price);
      }
    } catch (e) {}
    return '$' + (price / 1).toFixed(2);
  }

  // ---- Wire up the secondary image hover on a card ----------------------
  function setupSecondaryImage(card, product) {
    if (card.dataset.zargesSecondaryReady === '1') return;

    // Skip if the theme already rendered its own secondary image element.
    if (card.querySelector('.product-card__secondary')) {
      card.dataset.zargesSecondaryReady = '1';
      return;
    }

    if (!product.images || product.images.length < 2) return;

    if (window.matchMedia && window.matchMedia('(hover: none)').matches) return;

    var mediaWrap = card.querySelector('.product-card__media');
    if (!mediaWrap) return;

    var primaryImg = mediaWrap.querySelector('a img, .media img');
    if (!primaryImg) return;

    var THEME_TRANSITION = '0.5s cubic-bezier(0.3, 1, 0.3, 1)';
    primaryImg.style.transition = 'opacity ' + THEME_TRANSITION + ', transform ' + THEME_TRANSITION;
    primaryImg.style.transformOrigin = 'center center';

    var fitMode = 'cover';
    var fitPosition = 'center center';

    var secondaryImage = product.images[1];
    var secondaryImg = document.createElement('img');
    secondaryImg.className = 'zarges-secondary-img';
    secondaryImg.src = getImageUrl(secondaryImage, '720');
    secondaryImg.setAttribute('alt', secondaryImage.alt || '');
    secondaryImg.setAttribute('loading', 'lazy');
    secondaryImg.style.cssText = [
      'position: absolute !important',
      'top: 0 !important',
      'left: 0 !important',
      'right: 0 !important',
      'bottom: 0 !important',
      'width: 100% !important',
      'height: 100% !important',
      'max-width: none !important',
      'max-height: none !important',
      'object-fit: ' + fitMode + ' !important',
      'object-position: ' + fitPosition,
      'opacity: 0',
      'visibility: hidden',
      'transform: scale(1)',
      'transform-origin: center center',
      'transition: opacity ' + THEME_TRANSITION + ', visibility ' + THEME_TRANSITION + ', transform ' + THEME_TRANSITION,
      'pointer-events: none',
      'z-index: 1',
      'margin: 0',
      'padding: 0'
    ].join('; ');

    var mediaPosition = window.getComputedStyle(mediaWrap).position;
    if (mediaPosition === 'static') {
      mediaWrap.style.position = 'relative';
    }
    mediaWrap.style.overflow = 'hidden';
    mediaWrap.appendChild(secondaryImg);

    var onEnter = function() {
      primaryImg.style.opacity = '0';
      primaryImg.style.transform = 'scale(1.05)';
      secondaryImg.style.opacity = '1';
      secondaryImg.style.visibility = 'visible';
      secondaryImg.style.transform = 'scale(1)';
    };
    var onLeave = function() {
      primaryImg.style.opacity = '1';
      primaryImg.style.transform = 'scale(1)';
      secondaryImg.style.opacity = '0';
      secondaryImg.style.visibility = 'hidden';
      secondaryImg.style.transform = 'scale(1)';
      secondaryImg.style.objectPosition = fitPosition;
    };

    var BASE_PAN = 0;
    var MAX_PAN = 18;
    var panX = BASE_PAN;
    var panY = BASE_PAN;

    function recalcPanRange() {
      var iw = secondaryImg.naturalWidth;
      var ih = secondaryImg.naturalHeight;
      var rect = mediaWrap.getBoundingClientRect();
      if (!iw || !ih || !rect.width || !rect.height) return;

      var imgAspect = iw / ih;
      var tileAspect = rect.width / rect.height;

      if (imgAspect > tileAspect) {
        var ratio = imgAspect / tileAspect;
        panX = Math.min(MAX_PAN, BASE_PAN + (ratio - 1) * 35);
        panY = BASE_PAN;
      } else if (imgAspect < tileAspect) {
        var ratio2 = tileAspect / imgAspect;
        panY = Math.min(MAX_PAN, BASE_PAN + (ratio2 - 1) * 35);
        panX = BASE_PAN;
      } else {
        panX = BASE_PAN;
        panY = BASE_PAN;
      }
    }

    if (secondaryImg.complete) {
      recalcPanRange();
    } else {
      secondaryImg.addEventListener('load', recalcPanRange);
    }
    if (window.ResizeObserver) {
      var ro = new ResizeObserver(recalcPanRange);
      ro.observe(mediaWrap);
    }

    var onMove = function(e) {
      var rect = mediaWrap.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      var nx = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      var ny = ((e.clientY - rect.top) / rect.height) * 2 - 1;
      var px = 50 - nx * panX;
      var py = 50 - ny * panY;
      secondaryImg.style.objectPosition = px.toFixed(2) + '% ' + py.toFixed(2) + '%';
    };

    secondaryImg.style.transition += ', object-position 0.15s ease-out';

    card.addEventListener('mouseenter', onEnter);
    card.addEventListener('mouseleave', onLeave);
    card.addEventListener('mousemove', onMove);

    card.dataset.zargesSecondaryReady = '1';
  }

  // ---- Hover-to-play for product card videos -----------------------------
  function setupVideoHover() {
    var videos = document.querySelectorAll('.product-card__media video');
    videos.forEach(function(video) {
      if (video.dataset.zargesVideoReady === '1') return;

      video.muted = true;
      video.setAttribute('muted', '');
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');
      video.loop = true;
      if (!video.getAttribute('preload')) video.preload = 'metadata';

      var card = video.closest('.product-card') || video.closest('[data-usf-pid]') || video.parentElement;
      if (!card) return;

      var onEnter = function() {
        var p = video.play();
        if (p && typeof p.catch === 'function') p.catch(function() {});
      };
      var onLeave = function() {
        try { video.pause(); video.currentTime = 0; } catch (e) {}
      };

      card.addEventListener('mouseenter', onEnter);
      card.addEventListener('mouseleave', onLeave);

      video.dataset.zargesVideoReady = '1';
    });
  }

  // ---- Parallax pan on theme-rendered secondary images ------------------
  function setupThemeSecondaryParallax() {
    var wrappers = document.querySelectorAll('.product-card__secondary');
    wrappers.forEach(function(wrapper) {
      if (wrapper.dataset.zargesParallaxReady === '1') return;

      var img = wrapper.querySelector('img');
      if (!img) return;

      var card = wrapper.closest('.product-card') || wrapper.closest('.card') || wrapper.parentElement;
      if (!card) return;

      if (window.matchMedia && window.matchMedia('(hover: none)').matches) return;

      var BASE_PAN = 0;
      var MAX_PAN = 18;
      var panX = BASE_PAN;
      var panY = BASE_PAN;

      function recalcPanRange() {
        var iw = img.naturalWidth;
        var ih = img.naturalHeight;
        var rect = wrapper.getBoundingClientRect();
        if (!iw || !ih || !rect.width || !rect.height) return;

        var imgAspect = iw / ih;
        var tileAspect = rect.width / rect.height;

        if (imgAspect > tileAspect) {
          var ratio = imgAspect / tileAspect;
          panX = Math.min(MAX_PAN, BASE_PAN + (ratio - 1) * 35);
          panY = BASE_PAN;
        } else if (imgAspect < tileAspect) {
          var ratio2 = tileAspect / imgAspect;
          panY = Math.min(MAX_PAN, BASE_PAN + (ratio2 - 1) * 35);
          panX = BASE_PAN;
        } else {
          panX = BASE_PAN;
          panY = BASE_PAN;
        }
      }

      if (img.complete) {
        recalcPanRange();
      } else {
        img.addEventListener('load', recalcPanRange);
      }
      if (window.ResizeObserver) {
        var ro = new ResizeObserver(recalcPanRange);
        ro.observe(wrapper);
      }

      var existingTransition = img.style.transition || '';
      img.style.transition = (existingTransition ? existingTransition + ', ' : '') + 'object-position 0.15s ease-out';

      var onMove = function(e) {
        var rect = wrapper.getBoundingClientRect();
        if (!rect.width || !rect.height) return;
        var nx = ((e.clientX - rect.left) / rect.width) * 2 - 1;
        var ny = ((e.clientY - rect.top) / rect.height) * 2 - 1;
        var px = 50 - nx * panX;
        var py = 50 - ny * panY;
        img.style.setProperty('object-position', px.toFixed(2) + '% ' + py.toFixed(2) + '%', 'important');
      };
      var onLeave = function() {
        img.style.setProperty('object-position', '50% 50%', 'important');
      };

      card.addEventListener('mousemove', onMove);
      card.addEventListener('mouseleave', onLeave);

      wrapper.dataset.zargesParallaxReady = '1';
    });
  }

  // ---- Schedule video + parallax setup -----------------------------------
  function scheduleVideoSetup() {
    setupVideoHover();
    setupThemeSecondaryParallax();
    setTimeout(function() { setupVideoHover(); setupThemeSecondaryParallax(); }, 500);
    setTimeout(function() { setupVideoHover(); setupThemeSecondaryParallax(); }, 1500);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleVideoSetup);
  } else {
    scheduleVideoSetup();
  }

  // ---- Clean up any leftover mobile carousel injections from old code ---
  // The mobile carousel feature was removed; if any stale DOM exists from
  // before the cleanup, unwrap it so primary images render normally.
  function cleanupOldMobileCarousels() {
    var hosts = document.querySelectorAll('.zarges-mobile-carousel-host');
    hosts.forEach(function(host) { host.classList.remove('zarges-mobile-carousel-host'); });
    var carousels = document.querySelectorAll('.zarges-mobile-carousel');
    carousels.forEach(function(c) { c.parentNode && c.parentNode.removeChild(c); });
    var dots = document.querySelectorAll('.zarges-mobile-dots');
    dots.forEach(function(d) { d.parentNode && d.parentNode.removeChild(d); });
    var hostMediaTags = document.querySelectorAll('[data-zarges-carousel-ready]');
    hostMediaTags.forEach(function(el) { delete el.dataset.zargesCarouselReady; });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', cleanupOldMobileCarousels);
  } else {
    cleanupOldMobileCarousels();
  }

  // ---- Inject overlay + secondary image on all cards in the USF grid -----
  function enhanceCards() {
    cleanupOldMobileCarousels();

    var cards = document.querySelectorAll('#usf_container [data-usf-pid]');
    if (!cards.length) return;

    cards.forEach(function(card) {
      var pid = card.getAttribute('data-usf-pid');
      var product = findProductById(pid);
      if (!product) return;

      if (!card.querySelector('.zarges-spec-overlay')) {
        var specs = buildSpecs(product);
        if (specs.hasAny) {
          var mediaWrap = card.querySelector('.product-card__media');
          if (mediaWrap) {
            var computed = window.getComputedStyle(mediaWrap);
            if (computed.position === 'static') {
              mediaWrap.style.position = 'relative';
            }
            mediaWrap.style.overflow = 'hidden';
            mediaWrap.insertAdjacentHTML('beforeend', buildOverlayHtml(product, specs));
          }
        }
      }

      try {
        setupSecondaryImage(card, product);
      } catch (e) {
        console.warn('[Zarges] Secondary image setup failed for', pid, e);
      }
    });

    setupVideoHover();
    setupThemeSecondaryParallax();
  }

  // ---- Debounce + USF event wiring ---------------------------------------
  var debounceTimer = null;
  function scheduleEnhance() {
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(enhanceCards, 50);
  }

  waitForUsf(function() {
    scheduleEnhance();
    window.usf.event.add(['sr_updated', 'sr_viewChanged', 'rerender'], scheduleEnhance);

    var container = document.getElementById('usf_container');
    if (container && window.MutationObserver) {
      var observer = new MutationObserver(function(mutations) {
        for (var i = 0; i < mutations.length; i++) {
          if (mutations[i].addedNodes && mutations[i].addedNodes.length) {
            scheduleEnhance();
            return;
          }
        }
      });
      observer.observe(container, { childList: true, subtree: true });
    }
  });
})();
